import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import styles from "./TagsScreen.module.scss";
import classNames from "classnames";
import tagsService from "../../../apis/services/admin/tagsService";
import { toast } from "react-toastify";
import BaseModal from "../../../components/modals/baseModal/BaseModal";

// Preset colors for tags
const PRESET_COLORS = [
  "#EF4444", // Red
  "#F97316", // Orange
  "#EAB308", // Yellow
  "#22C55E", // Green
  "#06B6D4", // Cyan
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#6B7280", // Gray
  "#000000", // Black
];

// Helper: get contrast color (black/white) based on background
const getContrastColor = (hex) => {
  if (!hex) return "#000";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#000" : "#fff";
};

const TagsScreen = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState(PRESET_COLORS[5]);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tagsService.fetchTags();
      setTags(response?.data || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError("Greška pri učitavanju tagova");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Open modal for creating new tag
  const handleCreateClick = () => {
    setEditingTag(null);
    setFormName("");
    setFormDescription("");
    setFormColor(PRESET_COLORS[5]);
    setModalOpen(true);
  };

  // Open modal for editing tag
  const handleEditClick = (tag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormDescription(tag.description || "");
    setFormColor(tag.color || PRESET_COLORS[5]);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTag(null);
    setFormName("");
    setFormDescription("");
    setFormColor(PRESET_COLORS[5]);
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error("Naziv taga je obavezan");
      return;
    }

    setFormSubmitting(true);
    try {
      const data = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        color: formColor,
      };

      if (editingTag) {
        await tagsService.updateTag(editingTag.id, data);
        toast.success("Tag uspešno ažuriran");
      } else {
        await tagsService.createTag(data);
        toast.success("Tag uspešno kreiran");
      }

      handleCloseModal();
      fetchTags();
    } catch (err) {
      console.error("Error saving tag:", err);
      toast.error(editingTag ? "Greška pri ažuriranju taga" : "Greška pri kreiranju taga");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (tag) => {
    setTagToDelete(tag);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    try {
      await tagsService.deleteTag(tagToDelete.id);
      toast.success("Tag uspešno obrisan");
      setDeleteModalOpen(false);
      setTagToDelete(null);
      fetchTags();
    } catch (err) {
      console.error("Error deleting tag:", err);
      toast.error("Greška pri brisanju taga");
    }
  };

  // Render loading state
  const renderLoading = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorState}>
      <p>⚠️ {error}</p>
      <button className={styles.retryButton} onClick={fetchTags}>
        Pokušaj ponovo
      </button>
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>🏷️</div>
      <p>Nema kreiranih tagova</p>
      <button className={styles.createButton} onClick={handleCreateClick}>
        Kreiraj prvi tag
      </button>
    </div>
  );

  // Render tags table
  const renderTagsTable = () => (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Boja</th>
            <th>Naziv</th>
            <th>Opis</th>
            <th>Uređaja</th>
            <th>Akcije</th>
          </tr>
        </thead>
        <tbody>
          {tags.map((tag) => (
            <tr key={tag.id}>
              <td>
                <div
                  className={styles.colorPreview}
                  style={{ backgroundColor: tag.color }}
                />
              </td>
              <td>
                <span
                  className={styles.tagBadge}
                  style={{
                    backgroundColor: tag.color,
                    color: getContrastColor(tag.color),
                  }}
                >
                  {tag.name}
                </span>
              </td>
              <td>{tag.description || "-"}</td>
              <td>{tag.deviceCount ?? "-"}</td>
              <td>
                <div className={styles.actions}>
                  <button
                    className={styles.editButton}
                    onClick={() => handleEditClick(tag)}
                  >
                    ✏️ Izmeni
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteClick(tag)}
                  >
                    🗑️ Obriši
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Tagovi</h1>
          <button className={styles.createButton} onClick={handleCreateClick}>
            + Novi Tag
          </button>
        </div>

        {/* Content */}
        {loading && renderLoading()}
        {error && !loading && renderError()}
        {!loading && !error && tags.length === 0 && renderEmpty()}
        {!loading && !error && tags.length > 0 && renderTagsTable()}

        {/* Create/Edit Modal */}
        <BaseModal
          modalOpen={modalOpen}
          onClose={handleCloseModal}
          title={editingTag ? "Izmeni Tag" : "Novi Tag"}
        >
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Naziv *</label>
              <input
                type="text"
                className={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="npr. VIP, Problem, Novo..."
                maxLength={50}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Opis</label>
              <textarea
                className={styles.textarea}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Opcioni opis taga..."
                rows={3}
                maxLength={255}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Boja</label>
              <div className={styles.colorPicker}>
                {PRESET_COLORS.map((color) => (
                  <div
                    key={color}
                    className={classNames(
                      styles.colorOption,
                      formColor === color && styles.colorOptionSelected
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormColor(color)}
                  />
                ))}
              </div>
              <div className={styles.colorPreviewRow}>
                <span>Pregled:</span>
                <span
                  className={styles.tagBadge}
                  style={{
                    backgroundColor: formColor,
                    color: getContrastColor(formColor),
                  }}
                >
                  {formName || "Primer"}
                </span>
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCloseModal}
                disabled={formSubmitting}
              >
                Otkaži
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={formSubmitting || !formName.trim()}
              >
                {formSubmitting ? "Čuvanje..." : editingTag ? "Sačuvaj" : "Kreiraj"}
              </button>
            </div>
          </form>
        </BaseModal>

        {/* Delete Confirmation Modal */}
        <BaseModal
          modalOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setTagToDelete(null);
          }}
          title="Potvrda brisanja"
        >
          <div className={styles.deleteConfirmation}>
            <p>
              Da li ste sigurni da želite da obrišete tag{" "}
              <strong>{tagToDelete?.name}</strong>?
            </p>
            {tagToDelete?.deviceCount > 0 && (
              <p className={styles.deleteWarning}>
                ⚠️ Ovaj tag je dodeljen {tagToDelete.deviceCount} uređaju/ima.
                Brisanjem taga, on će biti uklonjen sa svih uređaja.
              </p>
            )}
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTagToDelete(null);
                }}
              >
                Otkaži
              </button>
              <button
                type="button"
                className={styles.deleteConfirmButton}
                onClick={handleConfirmDelete}
              >
                Obriši
              </button>
            </div>
          </div>
        </BaseModal>
      </div>
    </DashboardLayout>
  );
};

export default TagsScreen;


